import { ReactNode, useEffect } from "react";

import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";

import { PanelsView } from "@/panels/modules/panels/components/PanelsView";
import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";

import { useAsidePanelConfigSync, useMainPanelConfigSync, useTopbarConfigSync } from "../hooks/usePanelConfigSync";

interface PanelsProps {
    // Slot content
    leftSidebarSlot?: ReactNode;
    topbarSlot?: ReactNode;
    rightSidebarSlot?: ReactNode;
    leftTopASlot?: ReactNode;
    leftTopBSlot?: ReactNode;
    leftBottomASlot?: ReactNode;
    leftBottomBSlot?: ReactNode;
    rightTopASlot?: ReactNode;
    rightTopBSlot?: ReactNode;
    rightBottomASlot?: ReactNode;
    rightBottomBSlot?: ReactNode;

    // Incoming main panel configs
    leftSidebarConfig?: Partial<AsidePanelConfig>;
    topbarConfig?: Partial<TopbarConfig>;
    rightSidebarConfig?: Partial<AsidePanelConfig>;
    leftTopConfig?: Partial<MainPanelConfig>;
    leftBottomConfig?: Partial<MainPanelConfig>;
    rightTopConfig?: Partial<MainPanelConfig>;
    rightBottomConfig?: Partial<MainPanelConfig>;

    // Incoming global panel configs
    leftMainPanelDefaultSize?: number;
}

export const Panels = ({
    leftSidebarSlot,
    topbarSlot,
    rightSidebarSlot,
    leftTopASlot,
    leftTopBSlot,
    leftBottomASlot,
    leftBottomBSlot,
    rightTopASlot,
    rightTopBSlot,
    rightBottomASlot,
    rightBottomBSlot,

    leftSidebarConfig,
    topbarConfig,
    rightSidebarConfig,
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,

    leftMainPanelDefaultSize,
}: PanelsProps) => {
    // Get full configs from store
    const leftSidebarStoreConfig = usePanelsStore((s) => s.leftSidebar);
    const topbarStoreConfig = usePanelsStore((s) => s.topbar);
    const rightSidebarStoreConfig = usePanelsStore((s) => s.rightSidebar);
    const leftTopStoreConfig = usePanelsStore((s) => s.leftTop);
    const leftBottomStoreConfig = usePanelsStore((s) => s.leftBottom);
    const rightTopStoreConfig = usePanelsStore((s) => s.rightTop);
    const rightBottomStoreConfig = usePanelsStore((s) => s.rightBottom);

    const setAsidePanelConfig = usePanelsStore((s) => s.setAsidePanelConfig);
    const setTopbarConfig = usePanelsStore((s) => s.setTopbarConfig);
    const setMainPanelConfig = usePanelsStore((s) => s.setMainPanelConfig);

    const setLeftMainPanelDefaultSize = usePanelsStore((s) => s.setLeftMainPanelDefaultSize);
    const leftMainPanelDefaultSizeFromStore = usePanelsStore((s) => s.leftMainPanelDefaultSize);

    useAsidePanelConfigSync(leftSidebarConfig, "leftSidebar", setAsidePanelConfig);
    useTopbarConfigSync(topbarConfig, setTopbarConfig);
    useAsidePanelConfigSync(rightSidebarConfig, "rightSidebar", setAsidePanelConfig);
    useMainPanelConfigSync(leftTopConfig, "leftTop", setMainPanelConfig);
    useMainPanelConfigSync(leftBottomConfig, "leftBottom", setMainPanelConfig);
    useMainPanelConfigSync(rightTopConfig, "rightTop", setMainPanelConfig);
    useMainPanelConfigSync(rightBottomConfig, "rightBottom", setMainPanelConfig);

    useEffect(() => {
        if (typeof leftMainPanelDefaultSize !== "number") return;
        setLeftMainPanelDefaultSize(leftMainPanelDefaultSize);
    }, [leftMainPanelDefaultSize, setLeftMainPanelDefaultSize]);

    return (
        <PanelsView
            leftSidebar={leftSidebarSlot}
            topbar={topbarSlot}
            rightSidebar={rightSidebarSlot}
            leftTopA={leftTopASlot}
            leftTopB={leftTopBSlot}
            leftBottomA={leftBottomASlot}
            leftBottomB={leftBottomBSlot}
            rightTopA={rightTopASlot}
            rightTopB={rightTopBSlot}
            rightBottomA={rightBottomASlot}
            rightBottomB={rightBottomBSlot}
            leftSidebarConfig={leftSidebarStoreConfig}
            topbarConfig={topbarStoreConfig}
            rightSidebarConfig={rightSidebarStoreConfig}
            leftTopConfig={leftTopStoreConfig}
            leftBottomConfig={leftBottomStoreConfig}
            rightTopConfig={rightTopStoreConfig}
            rightBottomConfig={rightBottomStoreConfig}
            leftMainPanelDefaultSize={leftMainPanelDefaultSizeFromStore}
        />
    );
};
